import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Skeleton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import * as XLSX from 'xlsx';
import {
  View,
  ScrollView,
  TextInput,
  Text,
  Modal,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
} from "react-native";
import { CONSULTA_PIEZAS_COLORS, TEXT_COLORS, MUI_COLORS, MINI_RESUMEN_TABLA_COLORS } from "../../style/colors.js";
import { esFornitura } from "../../constants/Constants.js";
import { getGoldPrice } from "../../services/GoldPriceService.js";

// Función helper para formatear números con coma decimal
const formatNumberWithComma = (number) => {
  return number.toFixed(2).replace('.', ',');
};

// Función helper para normalizar valores que empiezan con punto
const normalizeDecimalValue = (value) => {
  if (!value) return 0;
  const stringValue = String(value).trim();
  // Si empieza con punto, agregar 0 al inicio
  if (stringValue.startsWith('.')) {
    return parseFloat('0' + stringValue);
  }
  return parseFloat(stringValue);
};

// Configuración de skeletons de carga
const SKELETON_CONFIG = {
  rows: 3,              // Número de filas de skeletons
  dataColumns: 6,      // Número de columnas de datos (etiquetas + valores, sin contar el botón)
  height: 20,           // Altura de los skeletons en px
  widthLabel: "100%",    // Ancho del skeleton de etiqueta
  widthValue: "100%",    // Ancho del skeleton de valor
  buttonSize: 24,       // Tamaño del skeleton del botón circular
};

// Función para calcular el oro fino de una aleación
const calcularOroFino = (aleacion, gramos) => {
  if (!aleacion || !gramos) return 0;
  
  const aleacionUpper = aleacion.toUpperCase();
  
  // Solo procesar aleaciones de oro (que contengan AU)
  if (!aleacionUpper.includes('AU')) return 0;
  
  // Calcular porcentaje de oro fino según la aleación
  let porcentajeOro = 0;
  
  if (aleacionUpper.includes('AU18KT') || aleacionUpper.includes('AU18K')) {
    porcentajeOro = 0.75; // 18K = 75% oro fino
  } else if (aleacionUpper.includes('AU14KT') || aleacionUpper.includes('AU14K')) {
    porcentajeOro = 0.583; // 14K = 58.3% oro fino
  } else if (aleacionUpper.includes('AU9KT') || aleacionUpper.includes('AU9K')) {
    porcentajeOro = 0.375; // 9K = 37.5% oro fino
  } else if (aleacionUpper.includes('AU22KT') || aleacionUpper.includes('AU22K')) {
    porcentajeOro = 0.917; // 22K = 91.7% oro fino
  } else if (aleacionUpper.includes('AU24KT') || aleacionUpper.includes('AU24K')) {
    porcentajeOro = 1.0; // 24K = 100% oro fino
  } else if (aleacionUpper.includes('AU')) {
    // Para otras aleaciones de oro, usar un porcentaje por defecto
    porcentajeOro = 0.75; // Por defecto 18K
  }
  
  return gramos * porcentajeOro;
};

const ComponentMiniResumenTabla = React.memo(({
  data = [],
  clienteSeleccionado,
  fechaInicio,
  fechaFin,
  translateText,
  lang = "es",
  onNavigateToMO,
  isLoading = false,
}) => {
  const [expandedPiezas, setExpandedPiezas] = useState(false);
  const [expandedFornituras, setExpandedFornituras] = useState(false);
  const [expandedAleaciones, setExpandedAleaciones] = useState(false);
  const [expandedGramosOro, setExpandedGramosOro] = useState(false);

  // Estados para el modal
  const [showModal, setShowModal] = useState(false);
  const [modalVisible, setModalVisible] = useState(true);
  const [modalData, setModalData] = useState({ titulo: "", mos: [] });
  const [searchTerm, setSearchTerm] = useState("");
  const [scrollPosition, setScrollPosition] = useState(0);
  
  // Estados para lazy loading
  const [visibleItems, setVisibleItems] = useState(100);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Estados para el precio del oro
  const [goldPrice, setGoldPrice] = useState({ usd: null, eur: null, usdPerGram: null, eurPerGram: null });
  const [isLoadingGold, setIsLoadingGold] = useState(false);

  // Cargar precio del oro al montar el componente
  useEffect(() => {
    const loadGoldPrice = async () => {
      setIsLoadingGold(true);
      try {
        const priceData = await getGoldPrice();
        setGoldPrice({
          usd: priceData.usd,
          eur: priceData.eur,
          usdPerGram: priceData.usdPerGram,
          eurPerGram: priceData.eurPerGram
        });
      } catch (error) {
        console.error('Error cargando precio del oro:', error);
      } finally {
        setIsLoadingGold(false);
      }
    };

    loadGoldPrice();
  }, []);

  // Calcular el resumen basado en los datos filtrados
  const miniResumen = useMemo(() => {
    // Filtrar datos según los criterios
    const datosFiltrados = data.filter((item) => {
      const clienteOk =
        !clienteSeleccionado || item.cliente?.trim() === clienteSeleccionado;

      const fechaEntrega = item.fechaEntrega?.split(" ")[0];

      let fechaOk = true;
      if (fechaInicio && fechaEntrega) {
        fechaOk = fechaOk && fechaEntrega >= fechaInicio;
      }
      if (fechaFin && fechaEntrega) {
        fechaOk = fechaOk && fechaEntrega <= fechaFin;
      }

      return clienteOk && fechaOk;
    });

    let piezasTotales = 0;
    let piezasTerminadas = 0;
    let precioTotalPiezas = 0;

    let forniturasTotal = 0;
    let forniturasTerminadas = 0;
    let precioTotalFornituras = 0;

    let mosConDatos = 0;
    let mosConProcesos = 0;

    // Objetos para almacenar procesos únicos, sus tiempos totales y precios totales
    const procesosPiezas = {};
    const procesosFornituras = {};
    const preciosPiezas = {};
    const preciosFornituras = {};
    
    // Objetos para almacenar procesos terminados y pendientes por separado
    const procesosPiezasTerminadas = {};
    const procesosPiezasPendientes = {};
    const preciosPiezasTerminadas = {};
    const preciosPiezasPendientes = {};
    
    const procesosForniturasTerminadas = {};
    const procesosForniturasPendientes = {};
    const preciosForniturasTerminadas = {};
    const preciosForniturasPendientes = {};

    // Objetos para almacenar aleaciones y sus gramos
    const aleacionesTotales = {};
    const aleacionesTerminadas = {};
    const aleacionesPendientes = {};
    
    // Objetos para almacenar oro fino por aleación
    const oroFinoTotales = {};
    const oroFinoTerminados = {};
    const oroFinoPendientes = {};

    const calcularPrecioMO = (item) => {
      if (!item.procesos || !Array.isArray(item.procesos)) {
        return 0;
      }

      const cantidad = parseFloat(item.cantidad_total || item.cantidad || 0);

      // Calcular el costo total de todos los procesos de esta MO
      const costoProcesos = item.procesos.reduce((total, proceso) => {
        const precio = parseFloat(proceso.precio || 0);
        const tiempo = parseFloat(proceso.tiempo || 0);
        const costoProceso = precio * tiempo; // Costo de este proceso
        return total + costoProceso;
      }, 0);

      // Multiplicar por la cantidad de piezas
      return costoProcesos * cantidad;
    };

    datosFiltrados.forEach((item) => {
      const cantidad = normalizeDecimalValue(item.cantidad_total || item.cantidad || 0);
      let cantidadPendiente = Math.max(0, normalizeDecimalValue(item.cantidadPendiente || 0)); // Evitar negativos
      let cantidadTerminada = Math.max(0, normalizeDecimalValue(item.cantidad_fabricada || item.cantidadTerminada || 0)); // Evitar negativos
      
      
      // Validar que pendientes + terminadas no excedan la cantidad total
      if (cantidadPendiente + cantidadTerminada > cantidad) {
        // Si excede, ajustar proporcionalmente
        const factor = cantidad / (cantidadPendiente + cantidadTerminada);
        cantidadPendiente = cantidadPendiente * factor;
        cantidadTerminada = cantidadTerminada * factor;
      }
      
      const terminada = cantidadTerminada; // Usar cantidadTerminada calculada
      const referencia = item.referencia || "";

      // Verificar si es fornitura (empieza por C, F, L, Z)
      const esFornituraItem = esFornitura(referencia);

     
      // Calcular precio si tiene procesos
      let precioItem = 0;
      if (
        item.procesos &&
        Array.isArray(item.procesos) &&
        item.procesos.length > 0
      ) {
        mosConProcesos++;
        precioItem = calcularPrecioMO(item);
        
        // Calcular y acumular tiempos y precios por proceso
        item.procesos.forEach(proceso => {

          const procesoKey = proceso.descripcion || proceso.nombre || 'Sin descripción';
          const tiempoProceso = parseFloat(proceso.tiempo || 0);
          const precioProceso = parseFloat(proceso.precio || 0);
          
          // Calcular tiempos y precios totales
          const tiempoTotalProceso = (procesoKey === "ENGASTADOS") ? Math.round(proceso.piedras_engastar * cantidad): tiempoProceso * cantidad;
          const precioTotalProceso = precioProceso * tiempoProceso * cantidad;

          // Calcular tiempos y precios terminados
          const tiempoTerminadoProceso = (procesoKey === "ENGASTADOS") ? Math.round(proceso.piedras_engastar * cantidadTerminada): tiempoProceso * cantidadTerminada;
          const precioTerminadoProceso = precioProceso * tiempoProceso * cantidadTerminada;
          
          // Calcular tiempos y precios pendientes
          const tiempoPendienteProceso = (procesoKey === "ENGASTADOS") ? Math.round(proceso.piedras_engastar * cantidadPendiente): tiempoProceso * cantidadPendiente;
          const precioPendienteProceso = precioProceso * tiempoProceso * cantidadPendiente;
          
          if (esFornituraItem) {
            // Totales
            if (!procesosFornituras[procesoKey]) {
              procesosFornituras[procesoKey] = 0;
              preciosFornituras[procesoKey] = 0;
            }
            procesosFornituras[procesoKey] += tiempoTotalProceso;
            preciosFornituras[procesoKey] += precioTotalProceso;
            
            // Terminadas
            if (!procesosForniturasTerminadas[procesoKey]) {
              procesosForniturasTerminadas[procesoKey] = 0;
              preciosForniturasTerminadas[procesoKey] = 0;
            }
            procesosForniturasTerminadas[procesoKey] += tiempoTerminadoProceso;
            preciosForniturasTerminadas[procesoKey] += precioTerminadoProceso;
            
            // Pendientes
            if (!procesosForniturasPendientes[procesoKey]) {
              procesosForniturasPendientes[procesoKey] = 0;
              preciosForniturasPendientes[procesoKey] = 0;
            }
            procesosForniturasPendientes[procesoKey] += tiempoPendienteProceso;
            preciosForniturasPendientes[procesoKey] += precioPendienteProceso;
          } else {
            // Totales
            if (!procesosPiezas[procesoKey]) {
              procesosPiezas[procesoKey] = 0;
              preciosPiezas[procesoKey] = 0;
            }
            procesosPiezas[procesoKey] += tiempoTotalProceso;
            preciosPiezas[procesoKey] += precioTotalProceso;
            
            // Terminadas
            if (!procesosPiezasTerminadas[procesoKey]) {
              procesosPiezasTerminadas[procesoKey] = 0;
              preciosPiezasTerminadas[procesoKey] = 0;
            }
            procesosPiezasTerminadas[procesoKey] += tiempoTerminadoProceso;
            preciosPiezasTerminadas[procesoKey] += precioTerminadoProceso;
            
            // Pendientes
            if (!procesosPiezasPendientes[procesoKey]) {
              procesosPiezasPendientes[procesoKey] = 0;
              preciosPiezasPendientes[procesoKey] = 0;
            }
            procesosPiezasPendientes[procesoKey] += tiempoPendienteProceso;
            preciosPiezasPendientes[procesoKey] += precioPendienteProceso;
          }
        });
      }

      // Separar según el tipo
      if (esFornituraItem) {
        forniturasTotal += cantidad;
        forniturasTerminadas += terminada;
        precioTotalFornituras += precioItem;
      } else {
        piezasTotales += cantidad;
        piezasTerminadas += terminada;
        precioTotalPiezas += precioItem;

        // Procesar aleaciones solo para piezas (no fornituras)
        // Solo procesar si tiene aleación y gramos válidos
        if (item.aleacion && item.aleacion.trim() !== "" && item.gramos && normalizeDecimalValue(item.gramos) > 0) {
          const aleacion = item.aleacion.trim();
          const gramos = normalizeDecimalValue(item.gramos);
          
          // Totales
          if (!aleacionesTotales[aleacion]) {
            aleacionesTotales[aleacion] = 0;
          }
          aleacionesTotales[aleacion] += gramos * cantidad;
          
          // Terminadas
          if (!aleacionesTerminadas[aleacion]) {
            aleacionesTerminadas[aleacion] = 0;
          }
          aleacionesTerminadas[aleacion] += gramos * cantidadTerminada;
          
          // Pendientes
          if (!aleacionesPendientes[aleacion]) {
            aleacionesPendientes[aleacion] = 0;
          }
          aleacionesPendientes[aleacion] += gramos * cantidadPendiente;
          
          // Calcular oro fino solo para aleaciones de oro
          const oroFino = calcularOroFino(aleacion, gramos);
          if (oroFino > 0) {
            // Totales
            if (!oroFinoTotales[aleacion]) {
              oroFinoTotales[aleacion] = 0;
            }
            oroFinoTotales[aleacion] += oroFino * cantidad;
            
            // Terminadas
            if (!oroFinoTerminados[aleacion]) {
              oroFinoTerminados[aleacion] = 0;
            }
            oroFinoTerminados[aleacion] += oroFino * cantidadTerminada;
            
            // Pendientes
            if (!oroFinoPendientes[aleacion]) {
              oroFinoPendientes[aleacion] = 0;
            }
            oroFinoPendientes[aleacion] += oroFino * cantidadPendiente;
          }
        }
      }
    });

    // Calcular sumas para la fila de totales
    const sumasPiezas = {
      tiempoTerminado: Object.values(procesosPiezasTerminadas).reduce((sum, tiempo) => sum + tiempo, 0),
      precioTerminado: Object.values(preciosPiezasTerminadas).reduce((sum, precio) => sum + precio, 0),
      tiempoPendiente: Object.values(procesosPiezasPendientes).reduce((sum, tiempo) => sum + tiempo, 0),
      precioPendiente: Object.values(preciosPiezasPendientes).reduce((sum, precio) => sum + precio, 0),
      tiempoTotal: Object.values(procesosPiezas).reduce((sum, tiempo) => sum + tiempo, 0),
      precioTotal: Object.values(preciosPiezas).reduce((sum, precio) => sum + precio, 0),
    };

    const sumasFornituras = {
      tiempoTerminado: Object.values(procesosForniturasTerminadas).reduce((sum, tiempo) => sum + tiempo, 0),
      precioTerminado: Object.values(preciosForniturasTerminadas).reduce((sum, precio) => sum + precio, 0),
      tiempoPendiente: Object.values(procesosForniturasPendientes).reduce((sum, tiempo) => sum + tiempo, 0),
      precioPendiente: Object.values(preciosForniturasPendientes).reduce((sum, precio) => sum + precio, 0),
      tiempoTotal: Object.values(procesosFornituras).reduce((sum, tiempo) => sum + tiempo, 0),
      precioTotal: Object.values(preciosFornituras).reduce((sum, precio) => sum + precio, 0),
    };

    const sumasAleaciones = {
      gramosTerminados: Object.values(aleacionesTerminadas).reduce((sum, gramos) => sum + gramos, 0),
      gramosPendientes: Object.values(aleacionesPendientes).reduce((sum, gramos) => sum + gramos, 0),
      gramosTotales: Object.values(aleacionesTotales).reduce((sum, gramos) => sum + gramos, 0),
    };
    
    const sumasOroFino = {
      gramosTerminados: Object.values(oroFinoTerminados).reduce((sum, gramos) => sum + gramos, 0),
      gramosPendientes: Object.values(oroFinoPendientes).reduce((sum, gramos) => sum + gramos, 0),
      gramosTotales: Object.values(oroFinoTotales).reduce((sum, gramos) => sum + gramos, 0),
    };

    return {
      piezasTotales,
      piezasTerminadas,
      precioTotalPiezas,
      forniturasTotal,
      forniturasTerminadas,
      precioTotalFornituras,
      precioTotal: precioTotalPiezas + precioTotalFornituras,
      totalMOs: datosFiltrados.length,
      mosConDatos,
      mosConProcesos,
      procesosPiezas,
      procesosFornituras,
      preciosPiezas,
      preciosFornituras,
      procesosPiezasTerminadas,
      procesosPiezasPendientes,
      preciosPiezasTerminadas,
      preciosPiezasPendientes,
      procesosForniturasTerminadas,
      procesosForniturasPendientes,
      preciosForniturasTerminadas,
      preciosForniturasPendientes,
      sumasPiezas,
      sumasFornituras,
      aleacionesTotales,
      aleacionesTerminadas,
      aleacionesPendientes,
      sumasAleaciones,
      oroFinoTotales,
      oroFinoTerminados,
      oroFinoPendientes,
      sumasOroFino,
    };
  }, [data, clienteSeleccionado, fechaInicio, fechaFin]);

  // Funciones para el modal optimizadas con useCallback
  const handleCellClick = useCallback((titulo, mos) => {
    if (mos.length > 0) {
      setModalData({ titulo, mos });
      setShowModal(true);
      setSearchTerm("");
      setScrollPosition(0);
      setVisibleItems(100); // Resetear a 100 items al abrir
    }
  }, [onNavigateToMO]);

  const handleMOClick = useCallback((mo) => {
    setShowModal(false);
    setModalVisible(true);
    setSearchTerm("");
    if (onNavigateToMO) {
      onNavigateToMO(mo);
    }
  }, [onNavigateToMO]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalVisible(true);
    setSearchTerm("");
    setScrollPosition(0);
    setVisibleItems(100); // Resetear a 100 items al cerrar
  }, []);

  const filteredMos = useMemo(() => {
    return modalData.mos.filter((item) => {
      const term = searchTerm.toLowerCase();
      return (
        item.mo?.toLowerCase().includes(term) ||
        item.cliente?.toLowerCase().includes(term) ||
        item.referencia?.toLowerCase().includes(term) ||
        String(item.cantidad)?.toLowerCase().includes(term)
      );
    });
  }, [modalData.mos, searchTerm]);

  // Items visibles para lazy loading
  const visibleMos = useMemo(() => {
    return filteredMos.slice(0, visibleItems);
  }, [filteredMos, visibleItems]);

  // Función para cargar más items
  const loadMoreItems = useCallback(() => {
    if (visibleItems < filteredMos.length && !isLoadingMore) {
      setIsLoadingMore(true);
      setTimeout(() => {
        setVisibleItems(prev => Math.min(prev + 25, filteredMos.length));
        setIsLoadingMore(false);
      }, 100); // Pequeño delay para evitar lag
    }
  }, [visibleItems, filteredMos.length, isLoadingMore]);

  // Componente optimizado para cada item de la lista
  const MOItem = React.memo(({ mo, index, onPress }) => (
    <TouchableOpacity
      style={styles.moItem}
      onPress={() => onPress(mo)}
    >
      <View style={styles.moItemContent}>
        <Text style={styles.moItemIndex}>{index + 1}.</Text>
        <View style={styles.moItemDetails}>
          <Text style={styles.moItemMo}>{mo.mo || translateText("Sin datos", lang)}</Text>
          <Text style={styles.moItemClient}>{mo.cliente} - {mo.referencia} - {parseFloat(mo.cantidad_total || mo.cantidad || 0).toFixed(0)}</Text>
        </View>
      </View>
    </TouchableOpacity>
  ));

  // Componente para el indicador de carga
  const LoadingIndicator = React.memo(() => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size="small" color={MINI_RESUMEN_TABLA_COLORS.COLOR_LOADING_SPINNER} />
      <Text style={styles.loadingText}>Cargando más...</Text>
    </View>
  ));

  // Componente ClickableCell para hacer las celdas clickeables
  const ClickableCell = ({ valor, titulo, mos, children, style = {} }) => (
    <TableCell
      align='center'
      style={{
        ...style,
        cursor: mos && mos.length > 0 ? "pointer" : "default",
        backgroundColor: mos && mos.length > 0 ? "rgba(255,255,255,0.9)" : style.backgroundColor || "transparent",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: mos && mos.length > 0 ? "rgba(255,255,255,1)" : style.backgroundColor || "transparent",
        },
      }}
      onClick={() => mos && mos.length > 0 && handleCellClick(titulo, mos)}>
      {children || valor}
    </TableCell>
  );

  const generateExcel = (tipo, datosCalculados = null) => {
    try {
      // Crear el workbook
      const wb = XLSX.utils.book_new();
      
      let excelData = [];
      let sheetName = '';
      
      if (tipo === 'piezas') {
        // Crear estructura de tabla para piezas
        excelData = [
          // Primera fila de headers principales
          ['PROCESO', 'Piezas pendientes', '', 'Piezas terminadas', '', 'Piezas totales', ''],
          // Segunda fila de sub-headers
          ['', 'MINUTOS', 'PRECIO', 'MINUTOS', 'PRECIO', 'MINUTOS', 'PRECIO']
        ];
        
        // Añadir datos de los procesos
        if (miniResumen.procesosPiezas && typeof miniResumen.procesosPiezas === 'object') {
          Object.entries(miniResumen.procesosPiezas)
            .filter(([proceso]) => proceso !== "MONTAJE")
            .forEach(([proceso, tiempoTotal]) => {
            const precioTotal = miniResumen.preciosPiezas && miniResumen.preciosPiezas[proceso] ? miniResumen.preciosPiezas[proceso] : 0;
            
            // Usar los datos reales calculados en useMemo
            const tiempoTerminado = miniResumen.procesosPiezasTerminadas[proceso] || 0;
            const tiempoPendiente = miniResumen.procesosPiezasPendientes[proceso] || 0;
            const precioTerminado = miniResumen.preciosPiezasTerminadas[proceso] || 0;
            const precioPendiente = miniResumen.preciosPiezasPendientes[proceso] || 0;
            
            excelData.push([
              proceso,
              parseFloat(tiempoPendiente),
              parseFloat(precioPendiente),
              parseFloat(tiempoTerminado),
              parseFloat(precioTerminado),
              parseFloat(tiempoTotal),
              parseFloat(precioTotal)
            ]);
          });
        }
        
        // Añadir fila de totales
        if (Object.keys(miniResumen.procesosPiezas).length > 0) {
          excelData.push([
            'TOTAL',
            parseFloat(miniResumen.sumasPiezas.tiempoPendiente),
            parseFloat(miniResumen.sumasPiezas.precioPendiente),
            parseFloat(miniResumen.sumasPiezas.tiempoTerminado),
            parseFloat(miniResumen.sumasPiezas.precioTerminado),
            parseFloat(miniResumen.sumasPiezas.tiempoTotal),
            parseFloat(miniResumen.sumasPiezas.precioTotal)
          ]);
        }
        
        sheetName = 'Resumen_Piezas';
      } else if (tipo === 'fornituras') {
        // Crear estructura de tabla para fornituras
        excelData = [
          // Primera fila de headers principales
          ['PROCESO', 'PENDIENTES', '', 'TERMINADAS', '', 'TOTALES', ''],
          // Segunda fila de sub-headers
          ['', 'MINUTOS', 'PRECIO', 'MINUTOS', 'PRECIO', 'MINUTOS', 'PRECIO']
        ];
        
        // Añadir datos de los procesos
        if (miniResumen.procesosFornituras && typeof miniResumen.procesosFornituras === 'object') {
          Object.entries(miniResumen.procesosFornituras)
            .filter(([proceso]) => proceso !== "MONTAJE")
            .forEach(([proceso, tiempoTotal]) => {
            const precioTotal = miniResumen.preciosFornituras && miniResumen.preciosFornituras[proceso] ? miniResumen.preciosFornituras[proceso] : 0;
            
            // Usar los datos reales calculados en useMemo
            const tiempoTerminado = miniResumen.procesosForniturasTerminadas[proceso] || 0;
            const tiempoPendiente = miniResumen.procesosForniturasPendientes[proceso] || 0;
            const precioTerminado = miniResumen.preciosForniturasTerminadas[proceso] || 0;
            const precioPendiente = miniResumen.preciosForniturasPendientes[proceso] || 0;
            
            excelData.push([
              proceso,
              parseFloat(tiempoPendiente),
              parseFloat(precioPendiente),
              parseFloat(tiempoTerminado),
              parseFloat(precioTerminado),
              parseFloat(tiempoTotal),
              parseFloat(precioTotal)
            ]);
          });
        }
        
        // Añadir fila de totales
        if (Object.keys(miniResumen.procesosFornituras).length > 0) {
          excelData.push([
            'TOTAL',
            parseFloat(miniResumen.sumasFornituras.tiempoPendiente),
            parseFloat(miniResumen.sumasFornituras.precioPendiente),
            parseFloat(miniResumen.sumasFornituras.tiempoTerminado),
            parseFloat(miniResumen.sumasFornituras.precioTerminado),
            parseFloat(miniResumen.sumasFornituras.tiempoTotal),
            parseFloat(miniResumen.sumasFornituras.precioTotal)
          ]);
        }
        
        sheetName = 'Resumen_Fornituras';
      } else if (tipo === 'aleaciones') {
        // Usar los datos calculados en tiempo real si están disponibles
        const oroFinoData = datosCalculados || miniResumen;
        
        // Crear estructura de tabla para aleaciones (oro fino)
        excelData = [
          // Primera fila de headers principales
          ['ALEACIÓN', 'PENDIENTES', '', 'TERMINADAS', '', 'TOTALES', ''],
          // Segunda fila de sub-headers
          ['', 'GRAMOS', 'PRECIO', 'GRAMOS', 'PRECIO', 'GRAMOS', 'PRECIO']
        ];
        
        // Añadir datos de aleaciones
        Object.keys(oroFinoData.oroFinoTotales || {}).forEach(aleacion => {
          const gramosPendiente = oroFinoData.oroFinoPendientes[aleacion] || 0;
          const gramosTerminado = oroFinoData.oroFinoTerminados[aleacion] || 0;
          const gramosTotal = oroFinoData.oroFinoTotales[aleacion] || 0;
          
          const precioPendiente = goldPrice.eurPerGram ? goldPrice.eurPerGram * gramosPendiente : 0;
          const precioTerminado = goldPrice.eurPerGram ? goldPrice.eurPerGram * gramosTerminado : 0;
          const precioTotal = goldPrice.eurPerGram ? goldPrice.eurPerGram * gramosTotal : 0;
          
          excelData.push([
            aleacion,
            parseFloat(gramosPendiente),
            parseFloat(precioPendiente),
            parseFloat(gramosTerminado),
            parseFloat(precioTerminado),
            parseFloat(gramosTotal),
            parseFloat(precioTotal)
          ]);
        });
        
        // Añadir fila de totales
        if (Object.keys(oroFinoData.oroFinoTotales || {}).length > 0) {
          excelData.push([
            'TOTAL',
            parseFloat(oroFinoData.sumasOroFino.gramosPendientes),
            parseFloat(goldPrice.eurPerGram ? goldPrice.eurPerGram * oroFinoData.sumasOroFino.gramosPendientes : 0),
            parseFloat(oroFinoData.sumasOroFino.gramosTerminados),
            parseFloat(goldPrice.eurPerGram ? goldPrice.eurPerGram * oroFinoData.sumasOroFino.gramosTerminados : 0),
            parseFloat(oroFinoData.sumasOroFino.gramosTotales),
            parseFloat(goldPrice.eurPerGram ? goldPrice.eurPerGram * oroFinoData.sumasOroFino.gramosTotales : 0)
          ]);
        }
        
        sheetName = 'Resumen_Aleaciones_Oro_Fino';
      }

      // Crear la hoja de trabajo
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      
      // Ajustar el ancho de las columnas
      const colWidths = [
        { wch: 20 },  // PROCESO
        { wch: 15 },  // MINUTOS
        { wch: 15 },  // PRECIO
        { wch: 15 },  // MINUTOS
        { wch: 15 },  // PRECIO
        { wch: 15 },  // MINUTOS
        { wch: 15 },  // PRECIO
      ];
      ws['!cols'] = colWidths;

      // Aplicar formatos de columna
      try {
        const range = XLSX.utils.decode_range(ws['!ref']);
        
        // Aplicar formato a todas las celdas de datos
        for (let R = 2; R <= range.e.r; R++) {
          for (let C = 0; C <= range.e.c; C++) {
            const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = ws[cellAddress];
            
            if (!cell || cell.v === 'TOTAL') continue;
            
            // Formato para columnas de minutos (columnas 1, 3, 5 - índice 0, 2, 4)
            if (C === 1 || C === 3 || C === 5) {
              cell.s = {
                ...cell.s,
                numFmt: "#,##0.00" // Formato de número con separadores de miles
              };
            }
            
            // Formato para columnas de precio (columnas 2, 4, 6 - índice 1, 3, 5)
            if (C === 2 || C === 4 || C === 6) {
              cell.s = {
                ...cell.s,
                numFmt: "#,##0.00  €" // Formato de moneda con símbolo  €
              };
            }
          }
        }
      } catch (error) {
        console.log('No se pudieron aplicar formatos al Excel:', error);
      }

      // Añadir la hoja al workbook
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Generar el archivo y descargarlo
      const fileName = `${sheetName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
    } catch (error) {
      console.error('❌ Error generando Excel:', error);
    }
  };

  return (
    <TableContainer
      component={Paper}
      style={{
        marginTop: 8,
        backgroundColor: MINI_RESUMEN_TABLA_COLORS.BACKGROUND_MAIN,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        borderTop: `1px solid ${MINI_RESUMEN_TABLA_COLORS.BORDER_MAIN}`,
        borderRadius: 4,
      }}>
      <Table size='small'>
        <TableBody>
          {isLoading ? (
            <>
              {/* Skeletons para las filas principales */}
              {[...Array(SKELETON_CONFIG.rows)].map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {/* Columna del botón expandir */}
                  <TableCell
                    align='center'
                    style={{
                      padding: "2px 4px",
                      borderBottom: `1px solid #9bb1bb`,
                      backgroundColor: "#ffffff",
                      width: "40px"
                    }}>
                    <Skeleton variant="circular" width={SKELETON_CONFIG.buttonSize} height={SKELETON_CONFIG.buttonSize} />
                  </TableCell>
                  {/* Columnas de datos (alternando etiqueta-valor) */}
                  {[...Array(SKELETON_CONFIG.dataColumns)].map((_, colIndex) => {
                    const isLabel = colIndex % 2 === 0; // Columnas pares son etiquetas, impares son valores
                    return (
                      <TableCell
                        key={colIndex}
                        align='center'
                        style={{
                          padding: isLabel ? "2px 8px" : "2px 4px",
                          borderBottom: `1px solid #9bb1bb`,
                          backgroundColor: isLabel ? "#F2F2F2" : "#ffffff",
                        }}>
                        <Skeleton 
                          variant="text" 
                          width={isLabel ? SKELETON_CONFIG.widthLabel : SKELETON_CONFIG.widthValue} 
                          height={SKELETON_CONFIG.height} 
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </>
          ) : (
            <>
          {/* Fila 1: Datos de PIEZAS */}
          <TableRow>
            <TableCell
              align='center'
              style={{
                fontSize: 13, 
                padding: "2px 4px", 
                borderBottom: `1px solid #9bb1bb`, 
                backgroundColor: "#ffffff",
                textAlign: "center",
                width: "40px"
              }}>
              <IconButton
                size="small"
                onClick={() => setExpandedPiezas(!expandedPiezas)}
                style={{ padding: 0, color: "#174045" }}
              >
                {expandedPiezas ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
              </IconButton>
            </TableCell>
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 8px",
                color: "#174045",
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                backgroundColor: "#F2F2F2",
              }}>
              {translateText
                ? translateText("Piezas Totales", lang)
                : "Piezas Totales"}
            </TableCell>
            <ClickableCell
              valor={Math.round(miniResumen.piezasTotales).toLocaleString()}
              titulo='Piezas Totales'
              mos={data.filter(item => {
                const referencia = item.referencia || "";
                return !esFornitura(referencia);
              })}
              style={{
                fontSize: 13,
                padding: "2px 4px",
                color: CONSULTA_PIEZAS_COLORS.SUCCESS_GREEN,
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: "500",
                position: 'relative',
              }}
            />
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 8px",
                color: "#174045",
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                backgroundColor: "#F2F2F2",
              }}>
              {translateText
                ? translateText("Piezas Terminadas", lang)
                : "Piezas Terminadas"}
            </TableCell>
            <ClickableCell
              valor={Math.round(miniResumen.piezasTerminadas).toLocaleString()}
              titulo='Piezas Terminadas'
              mos={data.filter(item => {
                const referencia = item.referencia || "";
                if (esFornitura(referencia)) return false;
                const cantidad = parseFloat(item.cantidad_total || item.cantidad || 0);
                const cantidadPendiente = parseFloat(item.cantidadPendiente || 0);
                const cantidadTerminada = cantidad - cantidadPendiente;
                return cantidadTerminada > 0;
              })}
              style={{
                fontSize: 13,
                padding: "2px 4px",
                color: CONSULTA_PIEZAS_COLORS.SUCCESS_GREEN,
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                position: 'relative',
              }}
            />
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 8px",
                color: "#174045",
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                backgroundColor: "#F2F2F2",
              }}>
              {translateText
                ? translateText("Precio Piezas", lang)
                : "Precio Piezas"}
            </TableCell>
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 4px",
                color: CONSULTA_PIEZAS_COLORS.SUCCESS_GREEN,
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                backgroundColor: "#ffffff",
                position: 'relative',
              }}>
              <div style={{ position: 'relative', width: '100%', height: '100%', color: CONSULTA_PIEZAS_COLORS.SUCCESS_GREEN }}>
                <span>{formatNumberWithComma(miniResumen.precioTotalPiezas)}  €</span>
              </div>
            </TableCell>
          </TableRow>

          {/* Fila expandible para procesos de PIEZAS */}
          <TableRow>
            <TableCell style={{ padding: 0, border: 0 }} colSpan={7}>
              <Collapse in={expandedPiezas} timeout="auto" unmountOnExit>
                <Table size="small" style={{ backgroundColor: MINI_RESUMEN_TABLA_COLORS.BACKGROUND_PIEZAS_EXPANDED }}>
                  <TableBody>
                    {/* Header de la tabla expandible */}
                    <TableRow>
                      <TableCell 
                        style={{ 
                          fontSize: 12, 
                          padding: "2px 4px", 
                          color: "#ffffff", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: "500",
                          backgroundColor: "#174045",
                          textAlign: "center",
                          width: "40px"
                        }}
                      >
                        {translateText("PROCESO", lang)}
                      </TableCell>
                      <TableCell 
                        colSpan={2}
                        style={{ 
                          fontSize: 12, 
                          padding: "2px 4px", 
                          color: "#ffffff", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: "500",
                          backgroundColor: "#174045",
                          textAlign: "center"
                        }}
                      >
                       {translateText("PENDIENTES", lang)}
                      </TableCell>
                      <TableCell 
                        colSpan={2}
                        style={{ 
                          fontSize: 12, 
                          padding: "2px 4px", 
                          color: "#ffffff", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: "500",
                          backgroundColor: "#174045",
                          textAlign: "center"
                        }}
                      >
                        {translateText("TERMINADAS", lang)}
                      </TableCell>
                      <TableCell 
                        colSpan={2}
                        style={{ 
                          fontSize: 12, 
                          padding: "2px 4px", 
                          color: "#ffffff", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: "500",
                          backgroundColor: "#174045",
                          textAlign: "center"
                        }}
                      >
                        {translateText("TOTALES", lang)}
                      </TableCell>
                    </TableRow>
                    
                    {/* Sub-header con columnas específicas */}
                    <TableRow>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center",
                          width: "40px"
                        }}
                      >
                        <button
                          onClick={() => generateExcel('piezas')}
                          style={{
                            backgroundColor: "#174045",
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            fontSize: 15,
                            cursor: 'pointer',
                            fontWeight: 'normal',
                          }}
                          title={translateText("Exportar a Excel", lang)}
                        >
                          📊
                        </button>
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("MINUTOS", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("PRECIO", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("MINUTOS", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("PRECIO", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("MINUTOS", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("PRECIO", lang)}
                      </TableCell>
                    </TableRow>
                    
                    {/* Datos de los procesos */}
                    {Object.keys(miniResumen.procesosPiezas).length > 0 ? (
                      Object.entries(miniResumen.procesosPiezas)
                        .filter(([proceso]) => proceso !== "MONTAJE")
                        .map(([proceso, tiempoTotal], index) => {

                   
                        const precioTotal = miniResumen.preciosPiezas[proceso] || 0;
                        
                        // Usar los datos reales calculados en useMemo
                        const tiempoTerminado = miniResumen.procesosPiezasTerminadas[proceso] || 0;
                        const tiempoPendiente = miniResumen.procesosPiezasPendientes[proceso] || 0;
                        const precioTerminado = miniResumen.preciosPiezasTerminadas[proceso] || 0;
                        const precioPendiente = miniResumen.preciosPiezasPendientes[proceso] || 0;
                        
                        return (
                        <TableRow key={index}>
                          <TableCell
                              align='left' 
                              style={{
                                fontSize: 13,
                                padding: "2px 4px",
                                color: "#174045",
                                borderBottom: `1px solid #9bb1bb`,
                                fontWeight: "500",
                                backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                                width: "40px"
                              }}
                          >
                            {proceso}
                          </TableCell>
                          <TableCell
                           align='center'
                           style={{
                             fontSize: 13,
                             padding: "2px 4px",
                             color: "#000000",
                             borderBottom: `1px solid #9bb1bb`,
                             fontWeight: "500",
                             backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                           }}
                          >
                            {tiempoPendiente.toLocaleString()}
                          </TableCell>
                          <TableCell
                           align='center'
                           style={{
                             fontSize: 13,
                             padding: "2px 4px",
                             color: "#000000",
                             borderBottom: `1px solid #9bb1bb`,
                             fontWeight: "500",
                             backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                           }}
                          >
                            {formatNumberWithComma(precioPendiente)}  €
                          </TableCell>
                          <TableCell
                           align='center'
                           style={{
                             fontSize: 13,
                             padding: "2px 4px",
                             color: "#000000",
                             borderBottom: `1px solid #9bb1bb`,
                             fontWeight: "500",
                             backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                           }}
                          >
                            {tiempoTerminado.toLocaleString()}
                          </TableCell>
                          <TableCell
                           align='center'
                           style={{
                             fontSize: 13,
                             padding: "2px 4px",
                             color: "#000000",
                             borderBottom: `1px solid #9bb1bb`,
                             fontWeight: "500",
                             backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                           }}
                          >
                            {formatNumberWithComma(precioTerminado)}  €
                          </TableCell>
                          <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "500",
                            backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                          }}
                          >
                            {Math.round(tiempoTotal).toLocaleString()}
                          </TableCell>
                          <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "500",
                            backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                          }}
                          >
                            {formatNumberWithComma(precioTotal)}  €
                          </TableCell>
                        </TableRow>
                      );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          style={{
                            fontSize: 9,
                            color: MINI_RESUMEN_TABLA_COLORS.TEXT_MUTED,
                            fontStyle: "italic",
                            padding: "4px 8px",
                            borderBottom: `1px solid ${MINI_RESUMEN_TABLA_COLORS.BORDER_LIGHT}`,
                            // backgroundColor: "#f8f9fa",
                          }}
                          colSpan={7}
                        >
                          No hay procesos registrados
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {/* Fila de totales */}
                    {Object.keys(miniResumen.procesosPiezas).length > 0 && (
                      <TableRow>
                        <TableCell
                          align='left'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#174045",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                            width: "40px"
                          }}
                        >
                          TOTAL
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                          }}
                        >
                          {Math.round(miniResumen.sumasPiezas.tiempoPendiente).toLocaleString()}
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                          }}
                        >
                          {formatNumberWithComma(miniResumen.sumasPiezas.precioPendiente)}  €
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                          }}
                        >
                          {Math.round(miniResumen.sumasPiezas.tiempoTerminado).toLocaleString()}
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                          }}
                        >
                          {formatNumberWithComma(miniResumen.sumasPiezas.precioTerminado)}  €
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                          }}
                        >
                          {Math.round(miniResumen.sumasPiezas.tiempoTotal).toLocaleString()}
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                          }}
                        >
                          {formatNumberWithComma(miniResumen.sumasPiezas.precioTotal)}  €
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Fila 2: Datos de FORNITURAS */}
          <TableRow>
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 4px",
                borderBottom: `1px solid #9bb1bb`,
                backgroundColor: "#ffffff",
                textAlign: "center",
                width: "40px"
              }}>
              <IconButton
                size="small"
                onClick={() => setExpandedFornituras(!expandedFornituras)}
                style={{ padding: 0, color: "#174045" }}
              >
                {expandedFornituras ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
              </IconButton>
            </TableCell>
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 8px",
                color: "#174045",
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                backgroundColor: "#F2F2F2",
              }}>
              {translateText
                ? translateText("Fornituras Total", lang)
                : "Fornituras Total"}
            </TableCell>
            <ClickableCell
              valor={Math.round(miniResumen.forniturasTotal).toLocaleString()}
              titulo='Fornituras Totales'
              mos={data.filter(item => {
                const referencia = item.referencia || "";
                return esFornitura(referencia);
              })}
              style={{
                fontSize: 13,
                padding: "2px 4px",
                color: MUI_COLORS.SECONDARY,
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: "500",
                position: 'relative',
              }}
            />
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 8px",
                color: "#174045",
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                backgroundColor: "#F2F2F2",
              }}>
              {translateText
                ? translateText("Fornituras Terminadas", lang)
                : "Fornituras Terminadas"}
            </TableCell>
            <ClickableCell
              valor={Math.round(miniResumen.forniturasTerminadas).toLocaleString()}
              titulo='Fornituras Terminadas'
              mos={data.filter(item => {
                const referencia = item.referencia || "";
                if (!esFornitura(referencia)) return false;
                const cantidad = parseFloat(item.cantidad_total || item.cantidad || 0);
                const cantidadPendiente = parseFloat(item.cantidadPendiente || 0);
                const cantidadTerminada = cantidad - cantidadPendiente;
                return cantidadTerminada > 0;
              })}
              style={{
                fontSize: 13,
                padding: "2px 4px",
                color: MUI_COLORS.SECONDARY,
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: "500",
                position: 'relative',
              }}
            />
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 8px",
                color: "#174045",
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                backgroundColor: "#F2F2F2",
              }}>
              {translateText
                ? translateText("Precio Fornituras", lang)
                : "Precio Fornituras"}
            </TableCell>
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 4px",
                color: MUI_COLORS.SECONDARY,
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: "500",
                backgroundColor: "#ffffff",
                position: 'relative',
              }}>
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <span>{formatNumberWithComma(miniResumen.precioTotalFornituras)}  €</span>
              </div>
            </TableCell>
          </TableRow>

          {/* Fila expandible para procesos de FORNITURAS */}
          <TableRow>
            <TableCell style={{ padding: 0, border: 0 }} colSpan={7}>
              <Collapse in={expandedFornituras} timeout="auto" unmountOnExit>
                <Table size="small" style={{ backgroundColor: MINI_RESUMEN_TABLA_COLORS.BACKGROUND_FORNITURAS_EXPANDED }}>
                  <TableBody>
                    {/* Header de la tabla expandible */}
                    <TableRow>
                      <TableCell 
                        style={{ 
                          fontSize: 12, 
                          padding: "2px 4px", 
                          color: "#ffffff", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: "500",
                          backgroundColor: "#174045",
                          textAlign: "center",
                          width: "40px"
                        }}
                      >
                        {translateText("PROCESO", lang)}
                      </TableCell>
                      <TableCell 
                        colSpan={2}
                        style={{ 
                          fontSize: 12, 
                          padding: "2px 4px", 
                          color: "#ffffff", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: "500",
                          backgroundColor: "#174045",
                          textAlign: "center"
                        }}
                      >
                        {translateText("PENDIENTES", lang)}
                      </TableCell>
                      <TableCell 
                        colSpan={2}
                        style={{ 
                          fontSize: 12, 
                          padding: "2px 4px", 
                          color: "#ffffff", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: "500",
                          backgroundColor: "#174045",
                          textAlign: "center"
                        }}
                      >
                        {translateText("TERMINADAS", lang)}
                      </TableCell>
                      <TableCell 
                        colSpan={2}
                        style={{ 
                          fontSize: 12, 
                          padding: "2px 4px", 
                          color: "#ffffff", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: "500",
                          backgroundColor: "#174045",
                          textAlign: "center"
                        }}
                      >
                        {translateText("TOTALES", lang)}
                      </TableCell>
                    </TableRow>
                    
                    {/* Sub-header con columnas específicas */}
                    <TableRow>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center",
                          width: "120px",
                          maxWidth: "120px"
                        }}
                      >
                        <button
                                                      onClick={() => generateExcel('fornituras')}
                            style={{
                              backgroundColor: "#174045",
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '2px 6px',
                              fontSize: 15,
                              cursor: 'pointer',
                              fontWeight: 'normal',
                            }}
                            title={translateText("Exportar a Excel", lang)}
                          >
                            📊
                          </button>
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("MINUTOS", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("PRECIO", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("MINUTOS", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("PRECIO", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("MINUTOS", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("PRECIO", lang)}
                      </TableCell>
                    </TableRow>
                    
                    {/* Datos de los procesos */}
                    {Object.keys(miniResumen.procesosFornituras).length > 0 ? (
                      Object.entries(miniResumen.procesosFornituras)
                        .filter(([proceso]) => proceso !== "MONTAJE")
                        .map(([proceso, tiempoTotal], index) => {
                        const precioTotal = miniResumen.preciosFornituras[proceso] || 0;
                        
                        // Usar los datos reales calculados en useMemo
                        const tiempoTerminado = miniResumen.procesosForniturasTerminadas[proceso] || 0;
                        const tiempoPendiente = miniResumen.procesosForniturasPendientes[proceso] || 0;
                        const precioTerminado = miniResumen.preciosForniturasTerminadas[proceso] || 0;
                        const precioPendiente = miniResumen.preciosForniturasPendientes[proceso] || 0;
                        
                        return (
                        <TableRow key={index}>
                          <TableCell
                            align='left' 
                            style={{
                              fontSize: 13,
                              padding: "2px 4px",
                              color: "#174045",
                              borderBottom: `1px solid #9bb1bb`,
                              fontWeight: "500",
                              backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                              width: "120px",
                              maxWidth: "120px"
                            }}
                          >
                            {proceso}
                          </TableCell>
                          <TableCell
                            align='center'
                            style={{
                              fontSize: 13,
                              padding: "2px 4px",
                              color: "#000000",
                              borderBottom: `1px solid #9bb1bb`,
                              fontWeight: "500",
                              backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                            }}
                          >
                            {tiempoPendiente.toLocaleString()}
                          </TableCell>
                          <TableCell
                            align='center'
                            style={{
                              fontSize: 13,
                              padding: "2px 4px",
                              color: "#000000",
                              borderBottom: `1px solid #9bb1bb`,
                              fontWeight: "500",
                              backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                            }}
                          >
                            {formatNumberWithComma(precioPendiente)}  €
                          </TableCell>
                          <TableCell
                           align='center'
                           style={{
                             fontSize: 13,
                             padding: "2px 4px",
                             color: "#000000",
                             borderBottom: `1px solid #9bb1bb`,
                             fontWeight: "500",
                             backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                           }}
                          >
                            {tiempoTerminado.toLocaleString()}
                          </TableCell>
                          <TableCell
                           align='center'
                           style={{
                             fontSize: 13,
                             padding: "2px 4px",
                             color: "#000000",
                             borderBottom: `1px solid #9bb1bb`,
                             fontWeight: "500",
                             backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                           }}
                          >
                            {formatNumberWithComma(precioTerminado)}  €
                          </TableCell>
                          <TableCell
                           align='center'
                           style={{
                             fontSize: 13,
                             padding: "2px 4px",
                             color: "#000000",
                             borderBottom: `1px solid #9bb1bb`,
                             fontWeight: "500",
                             backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                           }}
                          >
                            {Math.round(tiempoTotal).toLocaleString()}
                          </TableCell>
                          <TableCell
                           align='center'
                           style={{
                             fontSize: 13,
                             padding: "2px 4px",
                             color: "#000000",
                             borderBottom: `1px solid #9bb1bb`,
                             fontWeight: "500",
                             backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                           }}
                          >
                            {formatNumberWithComma(precioTotal)}  €
                          </TableCell>
                        </TableRow>
                      );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          style={{
                            fontSize: 9,
                            color: MINI_RESUMEN_TABLA_COLORS.TEXT_MUTED,
                            fontStyle: "italic",
                            padding: "4px 8px",
                            borderBottom: `1px solid ${MINI_RESUMEN_TABLA_COLORS.BORDER_LIGHT}`,
                            // backgroundColor: "#f8f9fa",
                          }}
                          colSpan={7}
                        >
                          No hay procesos registrados
                        </TableCell>
                      </TableRow>
                    )}
                    
                                        {/* Fila de totales */}
                    {Object.keys(miniResumen.procesosFornituras).length > 0 && (
                      <TableRow>
                        <TableCell
                          align='left'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#174045",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                            width: "120px",
                            maxWidth: "120px"
                          }}
                        >
                          TOTAL
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                          }}
                        >
                          {Math.round(miniResumen.sumasFornituras.tiempoPendiente).toLocaleString()}
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                          }}
                        >
                          {formatNumberWithComma(miniResumen.sumasFornituras.precioPendiente)}  €
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                          }}
                        >
                          {Math.round(miniResumen.sumasFornituras.tiempoTerminado).toLocaleString()}
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                          }}
                        >
                          {formatNumberWithComma(miniResumen.sumasFornituras.precioTerminado)}  €
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                          }}
                        >
                          {Math.round(miniResumen.sumasFornituras.tiempoTotal).toLocaleString()}
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "600",
                            backgroundColor: "#F2F2F2",
                          }}
                        >
                          {formatNumberWithComma(miniResumen.sumasFornituras.precioTotal)}  €
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Fila 3: Datos de ALEACIONES */}
          <TableRow>
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 4px",
                borderBottom: `1px solid #9bb1bb`,
                backgroundColor: "#ffffff",
                textAlign: "center",
                width: "40px"
              }}>
              <IconButton
                size="small"
                onClick={() => setExpandedAleaciones(!expandedAleaciones)}
                style={{ padding: 0, color: "#174045" }}
              >
                {expandedAleaciones ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
              </IconButton>
            </TableCell>
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 8px",
                color: "#174045",
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                backgroundColor: "#F2F2F2",
              }}>
              {translateText
                ? translateText("Gramos Total", lang)
                : "Gramos Total"}
            </TableCell>
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 4px",
                color: MINI_RESUMEN_TABLA_COLORS.COLOR_BLUE,
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                backgroundColor: "#ffffff",
              }}>
              {formatNumberWithComma(miniResumen.sumasAleaciones.gramosTotales)} g
            </TableCell>
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 8px",
                color: "#174045",
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                backgroundColor: "#F2F2F2",
              }}>
              {translateText
                ? translateText("Gramos Terminadas", lang)
                : "Gramos Terminadas"}
            </TableCell>
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 4px",
                color: MINI_RESUMEN_TABLA_COLORS.COLOR_BLUE,
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                backgroundColor: "#ffffff",
              }}>
              {formatNumberWithComma(miniResumen.sumasAleaciones.gramosTerminados)} g
            </TableCell>
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 8px",
                color: "#174045",
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                backgroundColor: "#F2F2F2",
              }}>
              {translateText
                ? translateText("Precio Oro/Gramo", lang)
                : "Precio Aleaciones"}
            </TableCell>
            <TableCell
              align='center'
              style={{
                fontSize: 13,
                padding: "2px 4px",
                color: MINI_RESUMEN_TABLA_COLORS.COLOR_BLUE,
                borderBottom: `1px solid #9bb1bb`,
                fontWeight: '500',
                backgroundColor: "#ffffff",
              }}>
              {isLoadingGold ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ marginRight: '8px' }}>⏳</span>
                  <span style={{ fontSize: '13px' }}>Cargando...</span>
                </div>
              ) : goldPrice.usdPerGram && goldPrice.eurPerGram ? (
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <div style={{ fontSize: '13px', color: MINI_RESUMEN_TABLA_COLORS.COLOR_BLUE}}>
                    {formatNumberWithComma(goldPrice.eurPerGram)} €
                  </div>
                  <div style={{ fontSize: '10px', color: '#666', fontWeight: 'normal' }}>
                    •
                  </div>
                  <div style={{ fontSize: '13px', color: MINI_RESUMEN_TABLA_COLORS.COLOR_PURPLE}}>
                    ${formatNumberWithComma(goldPrice.usdPerGram)}
                  </div>
                </div>
              ) : (
                <span style={{ color: '#999', fontSize: '13px' }}>N/A</span>
              )}
            </TableCell>
          </TableRow>
          

          {/* Fila expandible para aleaciones */}
          <TableRow>
            <TableCell style={{ padding: 0, border: 0 }} colSpan={7}>
              <Collapse in={expandedAleaciones} timeout="auto" unmountOnExit>
                <Table size="small" style={{ backgroundColor: MINI_RESUMEN_TABLA_COLORS.BACKGROUND_ALEACIONES_EXPANDED }}>
                  <TableBody>
                    {/* Header de la tabla expandible */}
                    <TableRow>
                      <TableCell 
                        style={{ 
                          fontSize: 12, 
                          padding: "2px 4px", 
                          color: "#ffffff", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: "500",
                          backgroundColor: "#174045",
                          textAlign: "center",
                          width: "40px"
                        }}
                      >
                        ALEACIÓN
                      </TableCell>
                      <TableCell 
                        colSpan={2}
                        style={{ 
                          fontSize: 12, 
                          padding: "2px 4px", 
                          color: "#ffffff", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: "500",
                          backgroundColor: "#174045",
                          textAlign: "center"
                        }}
                      >
                        {translateText("PENDIENTES", lang)}
                      </TableCell>
                      <TableCell 
                        colSpan={2}
                        style={{ 
                          fontSize: 12, 
                          padding: "2px 4px", 
                          color: "#ffffff", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: "500",
                          backgroundColor: "#174045",
                          textAlign: "center"
                        }}
                      >
                        {translateText("TERMINADAS", lang)}
                      </TableCell>
                      <TableCell 
                        colSpan={2}
                        style={{ 
                          fontSize: 12, 
                          padding: "2px 4px", 
                          color: "#ffffff", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: "500",
                          backgroundColor: "#174045",
                          textAlign: "center"
                        }}
                      >
                        {translateText("TOTALES", lang)}
                      </TableCell>
                    </TableRow>
                    
                    {/* Sub-header con columnas específicas */}
                    <TableRow>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center",
                          width: "40px"
                        }}
                      >
                        <button
                          onClick={() => generateExcel('aleaciones', miniResumen)}
                          style={{
                            backgroundColor: "#174045",
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            fontSize: 15,
                            cursor: 'pointer',
                            fontWeight: 'normal',
                          }}
                          title={translateText("Exportar a Excel", lang)}
                        >
                          📊
                        </button>
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("GRAMOS", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("PRECIO", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("GRAMOS", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("PRECIO", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("GRAMOS", lang)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          fontSize: 11, 
                          padding: "2px 4px", 
                          color: "#000000", 
                          borderBottom: `1px solid #9bb1bb`, 
                          fontWeight: '500',
                          backgroundColor: "#F2F2F2",
                          textAlign: "center"
                        }}
                      >
                        {translateText("PRECIO", lang)}
                      </TableCell>
                    </TableRow>
                    
                    {/* Datos de las aleaciones */}
                    {Object.keys(miniResumen.aleacionesTotales).length > 0 ? (
                      Object.entries(miniResumen.aleacionesTotales)
                        .sort(([a], [b]) => a.localeCompare(b)) // Ordenar alfabéticamente
                        .map(([aleacion, gramosTotal], index) => {
                        const gramosTerminado = miniResumen.aleacionesTerminadas[aleacion] || 0;
                        const gramosPendiente = miniResumen.aleacionesPendientes[aleacion] || 0;
                        
                        // Calcular oro fino para esta aleación
                        const oroFinoTotal = miniResumen.oroFinoTotales[aleacion] || 0;
                        const oroFinoTerminado = miniResumen.oroFinoTerminados[aleacion] || 0;
                        const oroFinoPendiente = miniResumen.oroFinoPendientes[aleacion] || 0;
                        
                        return (
                        <TableRow key={index}>
                          <TableCell
                            align='left' 
                            style={{
                              fontSize: 13,
                              padding: "2px 4px",
                              color: "#174045",
                              borderBottom: `1px solid #9bb1bb`,
                              fontWeight: "500",
                              backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                              width: "40px"
                            }}
                          >
                            {aleacion}
                          </TableCell>
                          <TableCell
                           align='center'
                           style={{
                             fontSize: 13,
                             padding: "2px 4px",
                             color: "#000000",
                             borderBottom: `1px solid #9bb1bb`,
                             fontWeight: "500",
                             backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                           }}
                          >
                            {formatNumberWithComma(gramosPendiente)} g
                          </TableCell>
                          <TableCell
                           align='center'
                           style={{
                             fontSize: 13,
                             padding: "2px 4px",
                             color: "#000000",
                             borderBottom: `1px solid #9bb1bb`,
                             fontWeight: "500",
                             backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                           }}
                          >
                            {isLoadingGold ? (
                              <span style={{ color: '#999', fontSize: '13px' }}>⏳</span>
                            ) : goldPrice.usdPerGram && goldPrice.eurPerGram ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                 <div style={{ fontSize: '13px', color: '#000000' }}>
                                   {formatNumberWithComma(goldPrice.eurPerGram * oroFinoPendiente)} €
                                 </div>
                                <div style={{ fontSize: '11px', color: MINI_RESUMEN_TABLA_COLORS.COLOR_PURPLE }}>
                                  ${formatNumberWithComma(goldPrice.usdPerGram * oroFinoPendiente)}
                                </div>
                              </div>
                            ) : (
                              <span style={{ color: '#999', fontSize: '13px' }}>N/A</span>
                            )}
                          </TableCell>
                          <TableCell
                           align='center'
                           style={{
                             fontSize: 13,
                             padding: "2px 4px",
                             color: "#000000",
                             borderBottom: `1px solid #9bb1bb`,
                             fontWeight: "500",
                             backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                           }}
                          >
                            {formatNumberWithComma(gramosTerminado)} g
                          </TableCell>
                          <TableCell
                           align='center'
                           style={{
                             fontSize: 13,
                             padding: "2px 4px",
                             color: "#000000",
                             borderBottom: `1px solid #9bb1bb`,
                             fontWeight: "500",
                             backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                           }}
                          >
                            {isLoadingGold ? (
                              <span style={{ color: '#999', fontSize: '13px' }}>⏳</span>
                            ) : goldPrice.usdPerGram && goldPrice.eurPerGram ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                 <div style={{ fontSize: '13px', color: '#000000'}}>
                                   {formatNumberWithComma(goldPrice.eurPerGram * oroFinoTerminado)} €
                                 </div>
                                <div style={{ fontSize: '11px', color: MINI_RESUMEN_TABLA_COLORS.COLOR_PURPLE }}>
                                  ${formatNumberWithComma(goldPrice.usdPerGram * oroFinoTerminado)}
                                </div>
                              </div>
                            ) : (
                              <span style={{ color: '#999', fontSize: '13px' }}>N/A</span>
                            )}
                          </TableCell>
                          <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "500",
                            backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                          }}
                          >
                            {formatNumberWithComma(gramosTotal)} g
                          </TableCell>
                          <TableCell
                          align='center'
                          style={{
                            fontSize: 13,
                            padding: "2px 4px",
                            color: "#000000",
                            borderBottom: `1px solid #9bb1bb`,
                            fontWeight: "500",
                            backgroundColor: index % 2 === 0 ? "#ffffff" : "#F2F2F2",
                          }}
                          >
                            {isLoadingGold ? (
                              <span style={{ color: '#999', fontSize: '13px' }}>⏳</span>
                            ) : goldPrice.usdPerGram && goldPrice.eurPerGram ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                 <div style={{ fontSize: '13px', color: '#000000' }}>
                                   {formatNumberWithComma(goldPrice.eurPerGram * oroFinoTotal)} €
                                 </div>
                                <div style={{ fontSize: '11px', color: MINI_RESUMEN_TABLA_COLORS.COLOR_PURPLE }}>
                                  ${formatNumberWithComma(goldPrice.usdPerGram * oroFinoTotal)}
                                </div>
                              </div>
                            ) : (
                              <span style={{ color: '#999', fontSize: '13px' }}>N/A</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          style={{
                            fontSize: 9,
                            color: MINI_RESUMEN_TABLA_COLORS.TEXT_MUTED,
                            fontStyle: "italic",
                            padding: "4px 8px",
                            borderBottom: `1px solid ${MINI_RESUMEN_TABLA_COLORS.BORDER_LIGHT}`,
                          }}
                          colSpan={7}
                        >
                          No hay aleaciones registradas
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {/* Fila de totales */}
                    {Object.keys(miniResumen.aleacionesTotales).length > 0 && (
                      <TableRow>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 14,
                            padding: "2px 4px",
                            color: MINI_RESUMEN_TABLA_COLORS.TEXT_BLACK,
                            borderBottom: `1px solid #9bb1bb`,
                            // fontWeight: "bold",
                            backgroundColor: MINI_RESUMEN_TABLA_COLORS.BACKGROUND_WHITE,
                            width: "40px"
                          }}
                        >
                          TOTAL
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 14,
                            padding: "2px 4px",
                            color: MINI_RESUMEN_TABLA_COLORS.TEXT_BLACK,
                            borderBottom: `1px solid #9bb1bb`,
                            // fontWeight: "bold",
                            backgroundColor: MINI_RESUMEN_TABLA_COLORS.BACKGROUND_WHITE,
                          }}
                        >
                          {formatNumberWithComma(miniResumen.sumasAleaciones.gramosPendientes)} g
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 14,
                            padding: "2px 4px",
                            color: MINI_RESUMEN_TABLA_COLORS.TEXT_BLACK,
                            borderBottom: `1px solid #9bb1bb`,
                            // fontWeight: "bold",
                            backgroundColor: MINI_RESUMEN_TABLA_COLORS.BACKGROUND_WHITE,
                          }}
                        >
                          {isLoadingGold ? (
                            <span style={{ color: '#999', fontSize: '10px' }}>⏳</span>
                          ) : goldPrice.usdPerGram && goldPrice.eurPerGram ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                               <div style={{ fontSize: '14px', color: 'Black'}}>
                                 {formatNumberWithComma(goldPrice.eurPerGram * miniResumen.sumasOroFino.gramosPendientes)}  €
                               </div>
                              <div style={{ fontSize: '12px', color: MINI_RESUMEN_TABLA_COLORS.COLOR_PURPLE }}>
                                ${formatNumberWithComma(goldPrice.usdPerGram * miniResumen.sumasOroFino.gramosPendientes)}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#999', fontSize: '10px' }}>N/A</span>
                          )}
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 14,
                            padding: "2px 4px",
                            color: MINI_RESUMEN_TABLA_COLORS.TEXT_BLACK,
                            borderBottom: `1px solid #9bb1bb`,
                            // fontWeight: "bold",
                            backgroundColor: MINI_RESUMEN_TABLA_COLORS.BACKGROUND_WHITE,
                          }}
                        >
                          {formatNumberWithComma(miniResumen.sumasAleaciones.gramosTerminados)} g
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 14,
                            padding: "2px 4px",
                            color: MINI_RESUMEN_TABLA_COLORS.TEXT_BLACK,
                            borderBottom: `1px solid #9bb1bb`,
                            // fontWeight: "bold",
                            backgroundColor: MINI_RESUMEN_TABLA_COLORS.BACKGROUND_WHITE,
                          }}
                        >
                          {isLoadingGold ? (
                            <span style={{ color: '#999', fontSize: '10px' }}>⏳</span>
                          ) : goldPrice.usdPerGram && goldPrice.eurPerGram ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                               <div style={{ fontSize: '14px', color: 'Black' }}>
                                 {formatNumberWithComma(goldPrice.eurPerGram * miniResumen.sumasOroFino.gramosTerminados)}  €
                               </div>
                              <div style={{ fontSize: '12px', color: MINI_RESUMEN_TABLA_COLORS.COLOR_PURPLE }}>
                                ${formatNumberWithComma(goldPrice.usdPerGram * miniResumen.sumasOroFino.gramosTerminados)}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#999', fontSize: '10px' }}>N/A</span>
                          )}
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 14,
                            padding: "2px 4px",
                            color: MINI_RESUMEN_TABLA_COLORS.TEXT_BLACK,
                            borderBottom: `1px solid #9bb1bb`,
                            // fontWeight: "bold",
                            backgroundColor: MINI_RESUMEN_TABLA_COLORS.BACKGROUND_WHITE,
                          }}
                        >
                          {formatNumberWithComma(miniResumen.sumasAleaciones.gramosTotales)} g
                        </TableCell>
                        <TableCell
                          align='center'
                          style={{
                            fontSize: 14,
                            padding: "2px 4px",
                            color: MINI_RESUMEN_TABLA_COLORS.TEXT_BLACK,
                            borderBottom: `1px solid #9bb1bb`,
                            // fontWeight: "bold",
                            backgroundColor: MINI_RESUMEN_TABLA_COLORS.BACKGROUND_WHITE,
                          }}
                        >
                          {isLoadingGold ? (
                            <span style={{ color: '#999', fontSize: '10px' }}>⏳</span>
                          ) : goldPrice.usdPerGram && goldPrice.eurPerGram ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                               <div style={{ fontSize: '14px', color: 'Black' }}>
                                 {formatNumberWithComma(goldPrice.eurPerGram * miniResumen.sumasOroFino.gramosTotales)} €
                               </div>
                              <div style={{ fontSize: '12px', color: MINI_RESUMEN_TABLA_COLORS.COLOR_PURPLE }}>
                                ${formatNumberWithComma(goldPrice.usdPerGram * miniResumen.sumasOroFino.gramosTotales)}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#999', fontSize: '10px' }}>N/A</span>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>
            </>
          )}

        </TableBody>
      </Table>

             <Modal
         visible={showModal && modalVisible}
         transparent={true}
         animationType="fade"
         onRequestClose={handleCloseModal}
       >
         <View style={styles.modalOverlay}>
           <View style={styles.modalContent}>
                           <Text style={styles.modalTitle}>{modalData.titulo}</Text>
              <Text style={styles.modalSubtitle}>
                {visibleItems < filteredMos.length 
                  ? `${visibleItems} de ${filteredMos.length}` 
                  : filteredMos.length
                } {translateText ? translateText("órdenes de trabajo encontradas", lang) : "órdenes de trabajo encontradas"}
                {visibleItems < filteredMos.length && (
                  <Text style={styles.lazyLoadingInfo}>
                    {" "}(Scroll para cargar más)
                  </Text>
                )}
              </Text>

             <TextInput
               style={styles.searchInput}
               placeholder={translateText ? translateText("Buscar por MO, cliente, referencia, etc.", lang) : "Buscar por MO, cliente, referencia, etc."}
               value={searchTerm}
               onChangeText={setSearchTerm}
             />

                           {filteredMos.length > 0 ? (
                <FlatList
                  data={visibleMos}
                  keyExtractor={(item, index) => `${item.mo}-${index}`}
                  renderItem={({ item, index }) => (
                    <MOItem
                      mo={item}
                      index={index}
                      onPress={handleMOClick}
                    />
                  )}
                  style={styles.flatList}
                  showsVerticalScrollIndicator={true}
                  removeClippedSubviews={true}
                  maxToRenderPerBatch={25}
                  windowSize={10}
                  initialNumToRender={100}
                  onEndReached={loadMoreItems}
                  onEndReachedThreshold={0.1}
                  ListFooterComponent={visibleItems < filteredMos.length ? LoadingIndicator : null}
                  getItemLayout={(data, index) => ({
                    length: 70, // Altura aproximada de cada item
                    offset: 70 * index,
                    index,
                  })}
                />
              ) : searchTerm ? (
                <View style={styles.noDataContainer}>
                  <Text style={styles.noDataText}>
                    {translateText ? translateText("No se encontraron resultados para", lang) : "No se encontraron resultados para"}: "{searchTerm}"
                  </Text>
                </View>
              ) : (
                <View style={styles.noDataContainer}>
                  <Text style={styles.noDataText}>{translateText ? translateText("No hay órdenes de trabajo disponibles", lang) : "No hay órdenes de trabajo disponibles"}</Text>
                </View>
              )}

             <View style={styles.modalActions}>
               <TouchableOpacity
                 style={styles.closeButton}
                 onPress={handleCloseModal}
               >
                 <Text style={styles.closeButtonText}>{translateText ? translateText("Cerrar", lang) : "Cerrar"}</Text>
               </TouchableOpacity>
             </View>
           </View>
         </View>
       </Modal>
    </TableContainer>
  );
});

const styles = {
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 15,
    padding: 20,
    width: '80%',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'normal',
    color: CONSULTA_PIEZAS_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
    marginBottom: 5,
  },
  modalSubtitle: {
    fontSize: 14,
    color: CONSULTA_PIEZAS_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    marginBottom: 15,
    fontStyle: 'italic',
  },
  searchInput: {
    width: '100%',
    height: 50,
    borderColor: MINI_RESUMEN_TABLA_COLORS.BORDER_LIGHT,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    marginBottom: 15,
    fontSize: 14,
  },
  flatList: {
    width: '100%',
    maxHeight: 400,
    paddingHorizontal: 5,
  },
  moItem: {
    paddingVertical: 10,
    paddingHorizontal: 15,
    marginBottom: 10,
    backgroundColor: MINI_RESUMEN_TABLA_COLORS.BACKGROUND_MAIN,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: MINI_RESUMEN_TABLA_COLORS.BORDER_LIGHT,
  },
  moItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  moItemIndex: {
    fontWeight: 'normal',
    color: CONSULTA_PIEZAS_COLORS.TEXT_TERTIARY,
    marginRight: 10,
  },
  moItemDetails: {
    flex: 1,
  },
  moItemMo: {
    fontSize: 16,
    fontWeight: 500,
    color: CONSULTA_PIEZAS_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
  },
  moItemClient: {
    fontSize: 12,
    color: CONSULTA_PIEZAS_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    marginTop: 5,
  },
  noDataContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 100,
  },
  noDataText: {
    fontSize: 14,
    color: CONSULTA_PIEZAS_COLORS.TEXT_LIGHT,
    fontStyle: 'italic',
  },
  loadingContainer: {
    padding: 15,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  loadingText: {
    fontSize: 12,
    color: CONSULTA_PIEZAS_COLORS.TEXT_TERTIARY,
    marginLeft: 8,
    fontStyle: 'italic',
  },
  lazyLoadingInfo: {
    fontSize: 11,
    color: CONSULTA_PIEZAS_COLORS.TEXT_PLACEHOLDER,
    fontStyle: 'italic',
  },
  modalActions: {
    width: '100%',
    marginTop: 15,
  },
  closeButton: {
    backgroundColor: CONSULTA_PIEZAS_COLORS.BACKGROUND_BUTTON,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
  },
  closeButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'normal',
  },
};

export default ComponentMiniResumenTabla;
